<script setup lang="ts">
  import { computed, ref } from 'vue';

  import type { Invoice } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { Separator } from '@ui/components/separator';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import InvoiceListWidgetStatus from '@/components/common/invoice-list-widget/InvoiceStatus.vue';
  import AttachmentSelectPopover from '@/components/emails/AttachmentSelectPopover.vue';
  import type { CreditMemoAttachments } from '@/composables/useCreditMemoAttachments';
  import { filterByNumber } from '@/composables/useCreditMemoAttachments';
  import type { InvoiceAttachments } from '@/composables/useInvoiceAttachments';
  import { filterInvoices } from '@/composables/useInvoiceAttachments';

  interface Props {
    creditMemoAttachments: CreditMemoAttachments;
    creditMemoEnabled: boolean;
    disabled?: boolean;
    invoiceAttachments: InvoiceAttachments;
    isAttachmentBudgetFull?: boolean;
    openFileInput?: () => void;
    relatedInvoices: Invoice[];
  }

  const props = withDefaults(defineProps<Props>(), {
    disabled: false,
    isAttachmentBudgetFull: false,
  });

  const openAttachInvoice = ref(false);
  const openAttachCreditMemo = ref(false);
  const invoiceSearch = ref('');
  const creditMemoSearch = ref('');

  const invoiceGroups = computed(() => [
    {
      heading: 'Related Invoices',
      items: props.relatedInvoices,
      showSelectAll: props.relatedInvoices.length > 1,
      allSelected: props.invoiceAttachments.allRelatedAttached.value,
      onSelectAll: () => props.invoiceAttachments.handleAttachAllInvoices(props.relatedInvoices),
    },
    {
      heading: 'Other Invoices',
      items: props.invoiceAttachments.otherInvoices.value,
      showSelectAll: props.invoiceAttachments.otherInvoices.value.length > 1,
      allSelected: props.invoiceAttachments.allOtherAttached.value,
      onSelectAll: () =>
        props.invoiceAttachments.handleAttachAllInvoices(
          props.invoiceAttachments.otherInvoices.value,
        ),
    },
  ]);

  const creditMemoGroups = computed(() => [
    {
      heading: 'Credit Memos',
      items: props.creditMemoAttachments.creditMemos.value,
      showSelectAll:
        props.creditMemoAttachments.creditMemos.value.filter((cm) => cm.pdf).length > 1,
      allSelected: props.creditMemoAttachments.allCreditMemosAttached.value,
      onSelectAll: props.creditMemoAttachments.handleAttachAllCreditMemos,
    },
  ]);
</script>

<template>
  <div class="flex flex-row items-center gap-1">
    <Tooltip v-if="openFileInput">
      <TooltipTrigger as-child>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="Attach files"
          :disabled="disabled || isAttachmentBudgetFull"
          @click="openFileInput"
        >
          <i class="fa far fa-paperclip-vertical" />
        </Button>
      </TooltipTrigger>

      <TooltipContent side="bottom">Attach files</TooltipContent>
    </Tooltip>

    <Separator v-if="openFileInput" class="h-5 my-auto mx-0" orientation="vertical" />

    <AttachmentSelectPopover
      v-model:open="openAttachInvoice"
      v-model:search="invoiceSearch"
      label="Attach invoice"
      placeholder="Search invoice..."
      empty-text="No invoices found."
      attached-tooltip="Invoice already attached"
      missing-file-tooltip="We don't have a file for this invoice."
      :filter-function="filterInvoices"
      icon="far fa-file-invoice-dollar"
      :groups="invoiceGroups"
      :get-key="(i: Invoice) => i.id"
      :get-label="(i: Invoice) => i.number ?? ''"
      :has-file="invoiceAttachments.hasInvoicePdf"
      :is-attached="invoiceAttachments.isInvoiceAttached"
      :is-pending="invoiceAttachments.isInvoicePending"
      :is-disabled="
        (i: Invoice) =>
          disabled || !invoiceAttachments.hasInvoicePdf(i) || invoiceAttachments.isInvoicePending(i)
      "
      @select="invoiceAttachments.toggleInvoiceAttachment"
      @update:search-term="invoiceAttachments.updateInvoiceSearch"
    >
      <template #item-meta="{ item }">
        <InvoiceListWidgetStatus :invoice="item" />
      </template>
    </AttachmentSelectPopover>

    <Separator v-if="creditMemoEnabled" class="h-5 my-auto mx-0" orientation="vertical" />

    <AttachmentSelectPopover
      v-if="creditMemoEnabled"
      v-model:open="openAttachCreditMemo"
      v-model:search="creditMemoSearch"
      label="Attach credit memo"
      placeholder="Search credit memo..."
      empty-text="No credit memos found."
      icon="far fa-file-plus-minus"
      attached-tooltip="Credit memo already attached — select to detach"
      missing-file-tooltip="We don't have a file for this credit memo."
      :filter-function="filterByNumber"
      :groups="creditMemoGroups"
      :get-key="(cm) => cm.id"
      :get-label="(cm) => cm.number ?? ''"
      :has-file="(cm) => !!cm.pdf"
      :is-attached="creditMemoAttachments.isCreditMemoAttached"
      :is-pending="creditMemoAttachments.isCreditMemoPending"
      :is-disabled="
        (cm) =>
          disabled ||
          !cm.pdf ||
          creditMemoAttachments.isCreditMemoPending(cm) ||
          (!creditMemoAttachments.isCreditMemoAttached(cm) && isAttachmentBudgetFull)
      "
      @select="creditMemoAttachments.toggleAttachCreditMemo"
    />
  </div>
</template>
